import { usePiPWindow } from 'components/PiP/PiPProvider';
import { leaveBreakoutRoomRequested } from 'features/breakout-rooms/actions';
import { leaveSession } from 'features/room/actions';
import { openModal } from 'features/modal/modalSlice';
import { selectIsBreakoutRoom } from 'features/breakout-rooms/breakoutRoomsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useFeatureFlag } from 'features/feature-set/useFeatureFlag';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { SDKEventListenerManager } from 'utils/sdk/eventLiseners';
import { useTranslation } from 'react-i18next';

export const useEndSessionControl = () => {
  const { t } = useTranslation('room');
  const dispatch = useAppDispatch();

  const { pipWindowOpen, closePipWindow } = usePiPWindow();

  const { hasPermissions } = usePermissions();
  const endSessionFeatureEnabled = useFeatureFlag('endSession');
  const leaveSessionFeatureEnabled = useFeatureFlag('leaveSession');

  const canEndSession = hasPermissions(PermissionTypes.endSession);
  const inBreakout: boolean = useAppSelector(selectIsBreakoutRoom);

  const endSessionAvailable = !inBreakout && endSessionFeatureEnabled && canEndSession;
  const leaveSessionAvailable = leaveSessionFeatureEnabled;

  const handleLeaveSession = SDKEventListenerManager.UICallbackAction('leaveSession', () => {
    dispatch(leaveSession());
  });

  const handleEndSession = () => {
    if (pipWindowOpen) {
      closePipWindow();
    }

    dispatch(openModal('endSessionConfirm'));
  };

  const handleLeaveBreakout = () => {
    dispatch(leaveBreakoutRoomRequested());
  };

  const renderSingleControlLabel = () => {
    if (endSessionAvailable) {
      return t('toolbar.end_session_options.end_session');
    }

    return t('toolbar.end_session_options.leave_session');
  };

  const singleControlHandler = () => {
    if (endSessionAvailable) {
      return handleEndSession;
    }
    return handleLeaveSession;
  };

  const renderMenuLabel = () => {
    if (leaveSessionAvailable && endSessionAvailable) {
      return t('toolbar.end_session_options.leave_or_end_session');
    }

    return t('toolbar.end_session_options.leave_session');
  };

  const availableControls = [leaveSessionAvailable, endSessionAvailable];
  const showNestedControls = availableControls.filter(Boolean).length > 1;

  return {
    leaveSession: handleLeaveSession,
    leaveBreakout: handleLeaveBreakout,
    endSession: handleEndSession,
    singleControlLabel: renderSingleControlLabel(),
    singleControlHandler: singleControlHandler(),
    menuLabel: renderMenuLabel(),
    icon: 'phone-disconnect',
    endSessionAvailable,
    leaveSessionAvailable,
    availableControls,
    showNestedControls,
  };
};
